/* EOS homepage. Shopify remains the source of truth for products and cart.
 * No quiz profiling, tracking calls, localStorage, or persisted message text.
 * Browser history stores only non-text browsing state. Cart POSTs never auto-retry. */
if (!customElements.get('eos-everyday-home')) {
  customElements.define('eos-everyday-home', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const signal = this.events.signal;
      this.key = this.dataset.sectionId || 'eos-home';
      this.root = this.dataset.root || '/';
      if (!this.root.endsWith('/')) this.root += '/';
      this.dialog = this.querySelector('[data-eos-dialog]');
      this.panel = this.querySelector('[data-eos-dialog-content]');
      this.host = this.querySelector('[data-eos-catalog-host]');
      this.loadStatus = this.querySelector('[data-eos-load-status]');
      if (!this.dialog || !this.panel || !this.host || !this.loadStatus) return;
      this.catalogs = new Map(); this.catalogPaths = []; this.recipient = 'gift';
      this.addBusy = false; this.historyClosing = false;
      this.querySelector('[data-eos-recipient-controls]')?.removeAttribute('hidden');
      this.querySelectorAll('[data-eos-note-controls]').forEach(el => el.hidden = false);
      this.addEventListener('click', event => this.onClick(event), {signal});
      this.addEventListener('change', event => this.onChange(event), {signal});
      this.addEventListener('submit', event => {
        if (event.target.matches('[data-eos-add]')) { event.preventDefault(); this.add(event.target); }
      }, {signal});
      this.querySelectorAll('[data-eos-feeling]').forEach(el => el.addEventListener('toggle', () => this.save(), {signal}));
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.closeQuick(); }, {signal});
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const r = this.dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.closeQuick();
      }, {signal});
      window.addEventListener('pagehide', () => { this.save(); this.resetScrollMode(); }, {signal});
      window.addEventListener('popstate', () => {
        if (this.historyClosing) { this.finishClose(); return; }
        if (this.dialog.open && history.state?.eosQuick !== this.key) this.closeQuick(false);
      }, {signal});
      window.addEventListener('pageshow', event => {
        if (event.persisted) { if (this.dialog.open) this.closeQuick(false); this.refreshCart().catch(() => {}); }
      }, {signal});
      this.restore().catch(() => {});
    }
    disconnectedCallback() {
      this.productRequest?.abort(); this.catalogRequest?.abort();
      clearTimeout(this.closeTimer);
      if (this.dialog?.open) { this.dialog.close(); document.body.style.overflow = this.savedOverflow || ''; }
      this.resetScrollMode(); this.events?.abort(); this.events = null;
    }
    localURL(value) {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin || !['http:', 'https:'].includes(url.protocol)) throw new Error('External link');
      url.searchParams.delete('section_id'); url.searchParams.delete('sections');
      return url;
    }
    resourceURL(value, kind) {
      const url = this.localURL(value);
      if (!url.pathname.includes('/' + kind + '/')) throw new Error('Unexpected resource');
      return url;
    }
    async section(value, name, selector, controller) {
      const url = this.localURL(value); url.searchParams.set('section_id', name);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const timeout = setTimeout(() => controller.abort(), 18000);
      try {
        const response = await fetch(url.pathname + url.search, {credentials:'same-origin', signal:controller.signal, headers:{Accept:'text/html'}});
        if (!response.ok) throw new Error('Section request failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = doc.querySelector(selector);
        if (!content || /Liquid (?:error|syntax error)/i.test(content.textContent)) throw new Error('Content unavailable');
        return document.importNode(content, true);
      } finally { clearTimeout(timeout); }
    }
    onClick(event) {
      if (!(event.target instanceof Element)) return;
      const target = event.target.closest('a,button');
      if (!target || !this.contains(target)) return;
      if (target.tagName === 'A' && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || target.target === '_blank')) return;
      if (target.matches('[data-eos-product]')) {
        if (!this.dialog.showModal || !window.fetch || this.historyClosing) return;
        event.preventDefault(); this.openQuick(target); return;
      }
      if (target.matches('[data-eos-close]')) { event.preventDefault(); this.closeQuick(); return; }
      if (target.matches('[data-eos-another]')) {
        event.preventDefault(); this.closeQuick(true, () => { this.setRecipient('gift'); this.jump(this.querySelector('[data-eos-guide]')); }); return;
      }
      if (target.matches('[data-eos-collection],[data-eos-more]')) {
        if (!window.fetch) return;
        event.preventDefault(); this.collectionTrigger = target; this.loadCollection(target.href, target.hasAttribute('data-eos-more')); return;
      }
      if (target.matches('[data-eos-collection-menu]')) { this.showCollectionMenu(); return; }
      if (target.matches('[data-eos-jump]')) {
        const element = document.getElementById(target.hash.slice(1));
        if (element) {
          event.preventDefault();
          if (element.classList.contains('eos-browse')) this.setMenuOpen(true);
          this.jump(element);
        }
        return;
      }
      if (target.matches('[data-eos-recipient]')) { this.setRecipient(target.dataset.eosRecipient); return; }
      if (target.matches('[data-eos-dismiss-feedback]')) {
        const box = this.querySelector('[data-eos-cart-feedback]');
        const restoreFocus = box.contains(document.activeElement);
        box.hidden = true;
        if (restoreFocus) {
          const next = [this.feedbackReturnFocus, this.originLink, document.getElementById('cart-icon-bubble')].find(el =>
            el instanceof HTMLElement && el.isConnected && el !== document.body && el.getClientRects().length && !el.matches(':disabled')
          );
          next?.focus();
        }
        this.feedbackReturnFocus = null;
        return;
      }
      if (target.matches('[data-eos-photo]')) {
        event.preventDefault();
        const image = this.panel.querySelector('.eos-shop__image');
        if (!image) return;
        image.removeAttribute('srcset'); image.removeAttribute('sizes'); image.src = target.href;
        image.alt = target.dataset.imageAlt || '';
        this.panel.querySelectorAll('[data-eos-photo]').forEach(el => el.removeAttribute('aria-current'));
        target.setAttribute('aria-current','true'); return;
      }
      if (target.matches('[data-eos-edit]')) { this.editNote(target.closest('[data-eos-letter]')); return; }
      if (target.matches('[data-eos-copy]')) { this.copyNote(target.closest('[data-eos-letter]')); return; }
      if (target.tagName === 'A') this.save();
    }
    onChange(event) {
      const el = event.target;
      if (el.matches('[data-eos-sort]') && this.catalogPaths.length) {
        const url = this.localURL(this.catalogPaths[0]); url.searchParams.set('sort_by', el.value); url.searchParams.delete('page');
        this.loadCollection(url.href, false, true);
      }
      if (el.matches('[name="id"],[data-eos-charm]') && el.closest('[data-eos-add]')) this.updatePrice(el.closest('[data-eos-add]'));
    }
    setRecipient(value) {
      this.recipient = value === 'self' ? 'self' : 'gift';
      this.querySelectorAll('[data-eos-recipient]').forEach(el => el.setAttribute('aria-pressed', String(el.dataset.eosRecipient === this.recipient)));
      this.querySelectorAll('[data-gift-label]').forEach(el => el.textContent = this.recipient === 'self' ? el.dataset.selfLabel : el.dataset.giftLabel);
      this.querySelectorAll('[data-eos-letter]').forEach(el => el.hidden = this.recipient === 'self');
      this.save();
    }
    editNote(letter, force) {
      const editor = letter.querySelector('[data-eos-note-editor]');
      const field = letter.querySelector('[data-eos-note]');
      const preview = letter.querySelector('[data-eos-note-preview]');
      const editing = force === undefined ? editor.hidden : force;
      editor.hidden = !editing; preview.hidden = editing; preview.textContent = field.value;
      const button = letter.querySelector('[data-eos-edit]');
      button.textContent = editing ? 'Done editing' : 'Make it your own'; button.setAttribute('aria-expanded', String(editing));
      if (editing) field.focus({preventScroll:true});
    }
    async copyNote(letter) {
      const field = letter.querySelector('[data-eos-note]'); const status = letter.querySelector('[data-eos-note-status]');
      if (!field.value.trim()) { this.editNote(letter,true); status.textContent = 'Add a few words, then copy when you’re ready.'; return; }
      try {
        if (!navigator.clipboard || !window.isSecureContext) throw new Error('Clipboard unavailable');
        await navigator.clipboard.writeText(field.value); status.textContent = 'Copied—ready for your card or text.';
      } catch (_) { this.editNote(letter,true); field.select(); status.textContent = 'Your words are selected. Use your device’s Copy command.'; }
    }
    jump(element) {
      if (!element) return;
      element.scrollIntoView({block:'start', behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      const heading = element.matches('[tabindex]') ? element : element.querySelector('h2[tabindex],h3[tabindex],a,button');
      heading?.focus({preventScroll:true});
    }
    setMenuOpen(open) {
      this.dataset.catalogMenu = open ? 'open' : 'closed';
      this.querySelector('[data-eos-collection-menu]')?.setAttribute('aria-expanded',String(open));
    }
    showCollectionMenu() {
      this.setMenuOpen(true); this.save();
      const families = this.querySelector('.eos-families');
      this.jump(families);
    }
    errorLink(container, message, href, label) {
      const p = document.createElement('p'); p.textContent = message;
      const a = document.createElement('a'); a.href = href; a.textContent = label; a.className = 'eos-text-link';
      container.replaceChildren(p,a);
    }
    async openQuick(link) {
      let url;
      try { url = this.resourceURL(link.href, 'products'); } catch (_) { location.assign(link.href); return; }
      if (this.dialog.open) return;
      this.productRequest?.abort(); const controller = this.productRequest = new AbortController();
      this.originLink = link; this.originScroll = window.scrollY; this.savedOverflow = document.body.style.overflow;
      this.save();
      this.panel.textContent = 'Opening this bracelet’s story…'; this.panel.setAttribute('aria-busy','true');
      this.dialog.querySelector('.eos-dialog__label').textContent = 'A closer look';
      this.dialog.showModal(); document.body.style.overflow = 'hidden';
      this.dialog.querySelector('[data-eos-close]').focus({preventScroll:true});
      try {
        this.savedScrollMode = history.scrollRestoration; history.scrollRestoration = 'manual';
        history.pushState({...history.state,eosQuick:this.key},'',location.href);
      } catch (_) {}
      try {
        const content = await this.section(url.href,'eos-home-product','[data-eos-product-panel]',controller);
        if (controller !== this.productRequest || !this.dialog.open) return;
        this.panel.replaceChildren(content); this.currentProductPath = url.pathname;
        this.dialog.querySelector('.eos-dialog__label').textContent = content.dataset.productTitle || 'A closer look';
        this.dialog.scrollTop = 0;
      } catch (_) {
        if (controller === this.productRequest && this.dialog.open) this.errorLink(this.panel,'This preview couldn’t load. Your browsing place is still here.',url.pathname + url.search,'Open this bracelet’s product page');
      } finally { if (controller === this.productRequest) this.panel.removeAttribute('aria-busy'); }
    }
    resetScrollMode() {
      if (this.savedScrollMode !== undefined) { try { history.scrollRestoration = this.savedScrollMode; } catch (_) {} this.savedScrollMode = undefined; }
    }
    closeQuick(updateHistory = true, afterClose = null) {
      if (!this.dialog?.open) { afterClose?.(); return; }
      this.productRequest?.abort(); this.productRequest = null;
      this.dialog.close(); document.body.style.overflow = this.savedOverflow || '';
      this.closeAction = afterClose;
      if (updateHistory && history.state?.eosQuick === this.key) {
        this.historyClosing = true;
        this.closeTimer = setTimeout(() => {
          if (history.state?.eosQuick === this.key) { const state = {...history.state}; delete state.eosQuick; try { history.replaceState(state,'',location.href); } catch (_) {} }
          this.finishClose();
        },700);
        history.back();
      } else this.finishClose();
    }
    finishClose() {
      clearTimeout(this.closeTimer); this.historyClosing = false;
      const action = this.closeAction; this.closeAction = null;
      requestAnimationFrame(() => {
        if (!this.isConnected || this.dialog.open) return;
        this.originLink?.focus({preventScroll:true}); window.scrollTo({top:this.originScroll || 0,behavior:'instant'});
        this.resetScrollMode(); this.save(); if (action) action();
      });
    }
    async loadCollection(value, append = false, force = false, restoring = false) {
      if (append && this.catalogLoading) return;
      let url;
      try { url = this.resourceURL(value,'collections'); } catch (_) { return; }
      const path = url.pathname + url.search;
      if (append && this.catalogPaths.includes(path)) return;
      this.catalogRequest?.abort(); const controller = this.catalogRequest = new AbortController();
      this.catalogLoading = true; this.host.setAttribute('aria-busy','true');
      this.loadStatus.textContent = append ? 'Finding a few more stories…' : 'Opening your collection…';
      if (!append && this.catalogPaths.length && this.host.firstElementChild) this.catalogs.set(this.catalogPaths[0],{node:this.host.firstElementChild,paths:[...this.catalogPaths]});
      try {
        const cached = !append && !force ? this.catalogs.get(path) : null;
        const content = cached?.node || await this.section(url.href,'eos-inline-collection','[data-eos-collection-panel]',controller);
        if (controller !== this.catalogRequest) return;
        if (append) {
          const grid = this.host.querySelector('[data-eos-catalog-grid]'); const newGrid = content.querySelector('[data-eos-catalog-grid]');
          const more = this.host.querySelector('[data-eos-catalog-more]'); const newMore = content.querySelector('[data-eos-catalog-more]');
          if (!grid || !newGrid || !more || !newMore) throw new Error('Missing collection');
          const seen = new Set(Array.from(grid.querySelectorAll('.eos-tile')).map(tile => tile.querySelector('[data-eos-product]')?.getAttribute('href')));
          const incoming = [...newGrid.children].filter(tile => !seen.has(tile.querySelector('[data-eos-product]')?.getAttribute('href')));
          grid.append(...incoming); more.replaceWith(newMore); this.catalogPaths.push(path);
          this.loadStatus.textContent = incoming.length ? incoming.length + ' more bracelets are ready below.' : 'You’ve reached the end of this selection.';
          if (!restoring && incoming[0]) { incoming[0].scrollIntoView({block:'nearest'}); incoming[0].querySelector('a')?.focus({preventScroll:true}); }
        } else {
          this.host.replaceChildren(content); this.catalogPaths = cached ? [...cached.paths] : [path];
          this.loadStatus.textContent = ''; this.querySelector('[data-eos-catalog-tools]').hidden = false;
          this.setMenuOpen(false);
          this.querySelectorAll('[data-eos-collection]').forEach(el => {
            try { if (this.localURL(el.href).pathname === url.pathname) el.setAttribute('aria-current','true'); else el.removeAttribute('aria-current'); } catch (_) {}
          });
          if (!restoring) this.jump(content);
        }
        if (!restoring) this.save();
        if (this.catalogs.size > 8) this.catalogs.delete(this.catalogs.keys().next().value);
      } catch (_) {
        if (controller === this.catalogRequest) {
          this.errorLink(this.loadStatus,'This collection couldn’t load here. Your previous selection is still available.',path,'Open the collection page');
          if (!restoring) this.jump(this.loadStatus);
        }
      } finally { if (controller === this.catalogRequest) { this.catalogLoading = false; this.host.removeAttribute('aria-busy'); } }
    }
    updatePrice(form) {
      const selected = form.querySelector('[name="id"]:checked') || form.querySelector('input[type="hidden"][name="id"]');
      const price = form.closest('[data-eos-product-panel]')?.querySelector('[data-eos-price]');
      if (selected?.dataset.price && price) price.textContent = selected.dataset.price;
      if (selected && this.currentProductPath) this.panel.querySelectorAll('a').forEach(link => {
        try { const url = this.localURL(link.href); if (url.pathname === this.currentProductPath) { url.searchParams.set('variant',selected.value); link.href = url.pathname + url.search; } } catch (_) {}
      });
      const total = form.querySelector('[data-eos-total]'); const charm = form.querySelector('[data-eos-charm]')?.selectedOptions[0];
      if (!total) return;
      total.textContent = '';
      if (selected && charm?.value) {
        const cents = Number(selected.dataset.cents) + Number(charm.dataset.cents);
        if (Number.isFinite(cents)) total.textContent = 'Bracelet + charm: ' + new Intl.NumberFormat(document.documentElement.lang || 'en',{style:'currency',currency:form.dataset.currency || 'USD'}).format(cents/100);
      }
    }
    feedback(message) {
      const box = this.querySelector('[data-eos-cart-feedback]'); if (!box) return;
      if (box.hidden && !box.contains(document.activeElement)) this.feedbackReturnFocus = document.activeElement;
      box.hidden = false; box.querySelector('[data-eos-cart-message]').textContent = message;
    }
    async add(form) {
      if (this.addBusy || !form.reportValidity()) return;
      this.addBusy = true;
      const button = form.querySelector('button[type="submit"]'); const status = form.querySelector('[data-eos-add-status]');
      const after = form.querySelector('[data-eos-after-add]'); const originalLabel = button.textContent;
      button.disabled = true; button.textContent = 'Adding…'; status.removeAttribute('data-error'); status.textContent = ''; after.hidden = true;
      const controller = new AbortController(); let timeout; let committed = false;
      try {
        const data = new FormData(form); const variant = data.get('id');
        if (!variant || !/^\d+$/.test(String(variant))) throw new Error('Choose a size before adding this bracelet.');
        const charm = form.querySelector('[data-eos-charm]');
        if (charm?.value) {
          const group = crypto.randomUUID(); data.delete('id'); data.delete('quantity');
          for (const [key,value] of [...data.entries()]) if (key.startsWith('properties[')) {
            data.delete(key); if (String(value).trim()) data.append('items[0]['+key.replace('properties[','properties]['),value);
          }
          data.set('items[0][id]',variant); data.set('items[0][quantity]','1'); data.set('items[0][properties][_eos_charm_group]',group);
          data.set('items[1][id]',charm.value); data.set('items[1][quantity]','1'); data.set('items[1][parent_id]',variant);
          data.set('items[1][properties][_eos_charm_group]',group); data.set('items[1][properties][_eos_charm_child]','true');
          const choice = form.querySelector('[name="id"]:checked');
          data.set('items[1][properties][For bracelet]',form.dataset.productTitle + (choice?.dataset.title ? ' — '+choice.dataset.title : ''));
        } else for (const [key,value] of [...data.entries()]) if (key.startsWith('properties[') && !String(value).trim()) data.delete(key);
        data.set('sections','cart-icon-bubble'); data.set('sections_url',location.pathname + location.search);
        timeout = setTimeout(() => controller.abort(),20000);
        const response = await fetch(this.root+'cart/add.js',{method:'POST',credentials:'same-origin',signal:controller.signal,headers:{Accept:'application/json'},body:data});
        let result;
        try { result = await response.json(); } catch (_) { throw new Error('We couldn’t confirm the addition. Please check your cart before trying again.'); }
        if (!response.ok || result.status) {
          if (response.status === 422) throw new Error((typeof result.description === 'string' ? result.description : 'That option could not be added.')+' Check your cart before trying again.');
          throw new Error('We couldn’t confirm the addition. Please check your cart before trying again.');
        }
        committed = true; status.textContent = 'Added to your cart. Your browsing place is right where you left it.'; after.hidden = false;
        if (!this.dialog.open || !this.panel.contains(form)) this.feedback((form.dataset.productTitle || 'Your bracelet')+' was added to your cart.');
        const iconHTML = result.sections?.['cart-icon-bubble'];
        if (iconHTML) {
          const icon = new DOMParser().parseFromString(iconHTML,'text/html').querySelector('.shopify-section');
          const target = document.getElementById('cart-icon-bubble'); if (icon && target) target.innerHTML = icon.innerHTML;
        }
        this.refreshCart().catch(() => {});
      } catch (error) {
        if (!committed) {
          status.dataset.error = 'true';
          status.textContent = error.name === 'AbortError' || error instanceof TypeError ? 'We couldn’t confirm the addition. Check your cart before trying again so you don’t add it twice.' : error.message;
          if (!this.dialog.open || !this.panel.contains(form)) this.feedback(status.textContent);
        }
      } finally { clearTimeout(timeout); this.addBusy = false; button.disabled = false; button.textContent = committed ? 'Add another of this bracelet' : originalLabel; }
    }
    async refreshCart() {
      const controller = new AbortController(); const timeout = setTimeout(() => controller.abort(),10000);
      try {
        const response = await fetch(this.root+'cart.js',{credentials:'same-origin',signal:controller.signal,headers:{Accept:'application/json'}});
        if (!response.ok) return;
        const cart = await response.json();
        if (Number.isInteger(cart.item_count)) this.querySelectorAll('[data-eos-cart-count]').forEach(el => el.textContent = '('+cart.item_count+')');
      } finally { clearTimeout(timeout); }
    }
    save() {
      if (this.restoring || this.historyClosing) return;
      try { history.replaceState({...history.state,eosHome:{key:this.key,paths:[...this.catalogPaths],open:[...this.querySelectorAll('[data-eos-feeling][open]')].map(el => el.dataset.eosFeeling),recipient:this.recipient,menu:this.dataset.catalogMenu,scroll:this.dialog?.open ? this.originScroll : window.scrollY}},'',location.href); } catch (_) {}
    }
    async restore() {
      const saved = history.state?.eosHome;
      if (!saved || saved.key !== this.key) return;
      this.restoring = true;
      try {
        this.setRecipient(saved.recipient);
        this.querySelectorAll('[data-eos-feeling]').forEach(el => el.open = Array.isArray(saved.open) && saved.open.includes(el.dataset.eosFeeling));
        for (const [index,path] of (Array.isArray(saved.paths) ? saved.paths.slice(0,30) : []).entries()) await this.loadCollection(path,index>0,false,true);
        if (saved.menu === 'open') this.setMenuOpen(true);
        requestAnimationFrame(() => window.scrollTo({top:Math.max(0,Number(saved.scroll)||0),behavior:'instant'}));
        if (history.state?.eosQuick === this.key) { const state = {...history.state}; delete state.eosQuick; history.replaceState(state,'',location.href); }
      } finally { this.restoring = false; }
    }
  });
}

